// MaturaArithmeticTermClosedTask.js
// Zadanie maturalne – zamknięte (ciąg arytmetyczny):
// dana różnica r i jeden wyraz a_m, szukamy innego wyrazu a_n (ABCD)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ======================= GENERATOR =======================

function createOriginalTask() {
  const options = [-12, -8, -4, 0].map((value, index) => ({
    value,
    latex: String(value),
    label: LETTERS[index],
    isCorrect: value === -8,
  }));

  return {
    type: "MaturaArithmeticTermClosedTask",
    data: {
      d: -4,
      n: 6,
      m: 10,
      aN: -8,
      aM: -24,
      k: 4,
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 16,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // różnica ciągu – małe liczby, także ujemne
  const d = pick([-5, -4, -3, -2, 2, 3, 4, 5]);

  // indeks szukanego wyrazu (np. 6)
  const n = pick([4, 5, 6, 7, 8]);

  // „odległość” między znanym a szukanym wyrazem (1–5 kroków)
  const k = pick([1, 2, 3, 4, 5]);

  // niech znany wyraz będzie późniejszy, jak w maturze:
  // m = n + k, a_m znamy, a_n liczymy „wstecz”
  const m = n + k;

  // szukany wyraz (ładna liczba z małego zakresu)
  const aN = pick([-20, -16, -12, -8, -4, 0, 4, 8, 12, 16, 20]);

  // a_m = a_n + k*d
  const aM = aN + k * d;

  // cztery odpowiedzi – poprawna + trzy bliskie
  const candidates = [
    aN, // poprawna
    aN + d,
    aN - d,
    aN + 2 * d,
  ];

  const uniqueValues = Array.from(new Set(candidates));
  while (uniqueValues.length < 4) {
    uniqueValues.push(uniqueValues[uniqueValues.length - 1] + 1);
  }

  const optionsShuffled = shuffle(
    uniqueValues.slice(0, 4).map((val) => ({
      value: val,
      latex: String(val),
    }))
  );

  const options = optionsShuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
    isCorrect: opt.value === aN,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaArithmeticTermClosedTask",
    data: {
      d,
      n,
      m,
      aN,
      aM,
      k,
      options,
      correctIndex,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { d, n, m, aM, options } = data;

  const rLatex = d < 0 ? `(${d})` : String(d);
  const aMLatex = aM < 0 ? `(${aM})` : String(aM);
  const nLatex = String(n);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Ciąg arytmetyczny <InlineMath math={'(a_n)'} /> jest określony dla
        każdej liczby naturalnej <InlineMath math={'n \\geq 1'} />.
      </p>
      <p>
        Różnica tego ciągu jest równa{" "}
        <InlineMath math={`r = ${rLatex}`} /> oraz{" "}
        <InlineMath math={`a_{${m}} = ${aMLatex}`} />
        <InlineMath math={'\\text{.}'} />
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Wyraz ciągu <InlineMath math={'(a_n)'} /> o numerze{" "}
        <InlineMath math={nLatex} /> jest równy:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { d, n, m, aN, aM } = data;

  const rLatex = d < 0 ? `(${d})` : String(d);
  const aMLatex = aM < 0 ? `(${aM})` : String(aM);
  const nLatex = String(n);
  const mLatex = String(m);

  const diffIndex = n - m; // u nas ujemne (bo n < m)
  const diffIndexLatex = String(diffIndex);

  const step1 = `a_{${nLatex}} = a_{${mLatex}} + (${nLatex}-${mLatex})\\cdot r`;
  const step2 = `a_{${nLatex}} = ${aMLatex} + (${diffIndexLatex})\\cdot ${rLatex}`;
  const step3 = `a_{${nLatex}} = ${aN}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy dane o ciągu arytmetycznym.
      </p>
      <p>
        Różnica ciągu jest równa{" "}
        <InlineMath math={`r = ${rLatex}`} />, a z treści mamy{" "}
        <InlineMath math={`a_{${mLatex}} = ${aMLatex}`} />
        <InlineMath math={'\\text{.}'} />
      </p>

      <p>
        <strong>Krok 2.</strong> Między wyrazami{" "}
        <InlineMath math={`a_{${mLatex}}`} /> i{" "}
        <InlineMath math={`a_{${nLatex}}`} /> jest{" "}
        <InlineMath math={String(Math.abs(m - n))} /> kroków o wartości{" "}
        <InlineMath math={'r'} />. Korzystamy ze wzoru łączącego dwa dowolne
        wyrazy ciągu arytmetycznego:
      </p>
      <BlockMath math={step1} />

      <p>Podstawiamy konkretne dane:</p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3.</strong> Obliczamy wyrażenie i otrzymujemy:
      </p>
      <BlockMath math={step3} />
      <p>
        Zatem szukany wyraz ciągu jest równy{" "}
        <InlineMath math={`${aN}\\text{.}`} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
